<template>
  <div
    v-if="deck"
    class="showcase-widget-component-container"
  >
    <dashboard-widget-header :title="config.name" />
    <div
      v-if="loading"
      class="loading-container"
    >
      <loading-bar />
    </div>
    <template v-else>
      <button
        v-if="featuredImage(card)"
        class="featured-image-button"
        tabindex="-1"
        @click="handleCardClick"
      >
        <img
          class="featured-image"
          :src="featuredImage(card).url"
          :alt="`Featured image for ${card.title}`"
        >
        <div
          v-if="card.isAttachmentCard"
          class="overlay"
        >
          <bookmark-button
            class="bookmark-button"
            :asset-name="card.title"
            :bookmarkable-type="card.assetType"
            :bookmarkable-id="asset && asset.id"
            :source="{ type: 'Card', id: card.id }"
            icon-variant="white"
          />
          <div class="play" />
        </div>
        <div
          v-else-if="card.isTopicCard"
          class="bookmark-button-backdrop"
        >
          <bookmark-button
            class="bookmark-button"
            :asset-name="card.title"
            :bookmarkable-type="card.assetType"
            :bookmarkable-id="asset && asset.id"
            icon-variant="white"
          />
        </div>
        <attachment-progress
          v-if="card.isAttachmentCard && state"
          :duration="asset.metadata && asset.metadata.duration"
          :watched="state.currentTimestamp"
        />
        <div
          v-else-if="assetInProgress || assetCompleted"
          class="asset-state-action"
        >
          <icon
            v-if="assetCompleted"
            name="check"
            variant="white"
            class="completed-icon"
          />
          {{ progressText }}
        </div>
      </button>
      <div class="card-details">
        <p
          v-if="metadata(card)"
          class="card-metadata"
        >
          <icon
            class="attachment-icon"
            :name="metadataIconName(card)"
            :variant="iconVariant"
          /> {{ metadata(card) }}
        </p>
        <button
          class="card-title"
          @click="handleCardClick"
        >
          {{ card.title }}
        </button>
        <p class="card-description">
          {{ card.escapedDescription }}
        </p>
      </div>
    </template>
  </div>
</template>

<script>
import AttachmentProgress from '@components/ui/attachment-progress';
import BookmarkButton from '@components/singletons/bookmarkButton';
import DashboardWidgetHeader from '@components/dashboard-widgets/header';
import Icon from '@components/singletons/icon';
import LoadingBar from '@components/singletons/loading-bar';

import CardMixin from '@/mixins/card';

export default {
  components: { AttachmentProgress, BookmarkButton, DashboardWidgetHeader, Icon, LoadingBar },
  mixins: [CardMixin],
  props: {
    config: {
      type: Object,
    },
    dashboard: {
      type: Object,
    },
  },
  data() {
    return {
      loading: true,
    };
  },
  computed: {
    asset() {
      return this.$store.getters[this.card.getter][this.card.assetId];
    },
    assetCompleted() {
      return this.cardAssetCompleted(this.card);
    },
    assetInProgress() {
      return this.cardAssetInProgress(this.card);
    },
    assetIsBookmarkable() {
      const bookmarkableAssetTypes = ['Attachment', 'Topic'];
      return bookmarkableAssetTypes.includes(this.card?.assetType);
    },
    card() {
      if (this.deck) {
        return this.deck.cards[0];
      }
    },
    deck() {
      if (this.config.decks) {
        const sortedDecks = [...this.config.decks].sort((a, b) => a.order - b.order);
        return sortedDecks.map(deck => this.$store.getters['decks/get'][deck.id]).filter(d => d)[0];
      };
    },
    iconVariant() {
      if (this.assetInProgress || this.assetCompleted) {
        return 'mastery';
      } else {
        return 'content-secondary';
      }
    },
    progressText() {
      if (this.assetCompleted) {
        return 'Complete';
      } else if (this.assetInProgress) {
        return 'Continue';
      }
    },
    state() {
      return this.cardAssetState(this.card);
    },
  },
  created() {
    this.deck.fetchAssets(this.$store).then(() => this.loading = false);
  },
  methods: {
    handleCardClick() {
      this.$analytics.events.viewedCard(this.card, this.deck, this.config);
      this.$analytics.events.viewedWidget(this.config, this.card, this.deck, this.dashboard);
      this.openCard(this.card, this.deck);
    },
  },
};
</script>

<style lang="scss" scoped>
.showcase-widget-component-container {
  @include padded-widget;
  display: flex;
  flex-wrap: wrap;

  /deep/ .dashboard-widget-header {
    width: 100%;
  }

  .loading-container {
    height: rem-calc(200);
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .featured-image-button {
    position: relative;
    background: transparent;
    border: 0;
    border-radius: 8px;
    max-height: 100%;
    overflow: hidden;
    padding: 0;
    width: rem-calc(376);
    transition: 0.25s all ease-in-out;
    border: 1px solid #cccccc;
    @include zoom-widget;

    &:hover {
      .overlay,
      .bookmark-button-backdrop {
        background: rgba(36, 34, 34, .6);
        display: flex;
      }

      ~ .card-details .card-title {
        text-decoration: underline;
      }
    }

    .featured-image {
      width: 100%;
      height: 100%;
      border-radius: 8px;
    }

    .overlay {
      @include widget-overlay;
      border-radius: 8px;

      .bookmark-button {
        position: absolute;
        right: 12px;
        top: 12px;
      }
    }

    .bookmark-button-backdrop {
      align-items: center;
      border-radius: 4px;
      display: none;
      justify-content: center;
      padding: 4px;
      position: absolute;
      right: 8px;
      top: 8px;
    }

    .asset-state-action {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      background-color: #000000;
      color: #ffffff;
      text-align: center;
      font-weight: $bold;
      padding: rem-calc(10) 0;
      font-size: rem-calc(12);

      .completed-icon {
        width: rem-calc(12);
        height: rem-calc(12);
        padding: 2px;
        border-radius: 100%;
        background-color: #00b46d;
        vertical-align: text-top;
        margin-right: rem-calc(4);
      }
    }
  }

  .card-details {
    box-sizing: border-box;
    max-width: 50%;
    padding: 0 0 0 rem-calc(24);
    margin: auto 0;

    .card-metadata {
      align-items: center;
      color: #767676;
      display: flex;
      font-size: rem-calc(12);
      letter-spacing: 0.5px;
      margin: 0 0 rem-calc(8);

      .attachment-icon {
        margin-right: rem-calc(8);
        height: rem-calc(13);
        width: rem-calc(13);
      }
    }

    .card-title {
      margin: 0;
      padding: 0;
      font-weight: $bold;
      font-size: rem-calc(16);
      line-height: 1.5;
      text-align: left;
      background: transparent;
      border: none;

      &:hover,
      &:focus {
        text-decoration: underline;
      }
    }

    .card-description {
      color: #767676;
      margin: rem-calc(8) 0 0;
      padding: 0;
      line-height: 1.5;
    }
  }

  @include breakpoint(medium) {
    flex-direction: column;

    .featured-image-button {
      width: 100%;
    }

    .card-details {
      max-width: 100%;
      padding: rem-calc(16) 0 0;
    }
  }
}
</style>
